<template>
  <v-dialog 
    v-model="dialog" 
    persistent
    max-width="450px"
  >
    <template v-slot:activator="{ on }">
      <v-btn 
        color="hrissecondary"
        class="white--text px-10 mx-1"
        v-on="on"
      >
        Resign
      </v-btn>
    </template>

    <material-card
      :offset="10"
      :full-width="true"
      color="hrissecondary"
      title="Separation"
      text="Employee Separation"
      class="mb-0"
      :loading="loading"
    >
      <v-container v-if="dialog && !loading">
        <v-row>
          <v-col
            cols="12"
            class="pb-0"
          >
            <v-autocomplete
              v-model="separationType"
              v-validate="'required'"
              :error-messages="errors.collect('separationType')"
              data-vv-name="separationType"        
              data-vv-as="separation type"
              label="Separation Type"
              :items="separationTypes"   
              item-text="name"
              item-value="id"
              hide-no-data
              hide-selected
              outlined
            />
          </v-col>
          
          <v-col
            cols="12"
            class="pb-0"
          >
            <v-menu
              v-model="lastWorkingDateMenu"
              :close-on-content-click="false"
              :nudge-right="40"
              transition="scale-transition"
              offset-y
              min-width="290px"
            >
              <template v-slot:activator="{ on }">
                <v-text-field
                  v-model="lastWorkingDateDisplay"
                  v-validate="'required'"    
                  :error-messages="errors.collect('lastWorkingDateDisplay')"
                  data-vv-name="lastWorkingDateDisplay"
                  :class="{
                    'required':!lastWorkingDateDisplay
                  }"
                  class="label-position"
                  outlined
                  readonly
                  label="Last Working Date"
                  data-vv-as="last working date"
                  v-on="on"
                >
                  <template v-slot:prepend-inner>
                    <v-icon>
                      mdi-calendar
                    </v-icon>
                  </template>
                </v-text-field>
              </template> 
              <v-date-picker
                v-model="lastWorkingDate"
                :min="separationMinDate"
                @input="lastWorkingDateMenu = false"
              />
            </v-menu>
          </v-col>

          <v-col
            cols="12"
            class="pb-0"
          >
            <v-menu
              v-model="effectiveDateMenu"
              :close-on-content-click="false"
              :nudge-right="40"
              transition="scale-transition"
              offset-y
              min-width="290px"
            >
              <template v-slot:activator="{ on }">
                <v-text-field
                  v-model="effectiveDateDisplay"
                  v-validate="'required'"
                  :error-messages="errors.collect('effectiveDateDisplay')"
                  data-vv-name="effectiveDateDisplay"
                  label="Effective Date"
                  outlined
                  :class="{
                    'required':!effectiveDateDisplay
                  }"
                  class="label-position"
                  data-vv-as="effective date"
                  readonly
                  v-on="on"
                >
                  <template v-slot:prepend-inner>
                    <v-icon>
                      mdi-calendar
                    </v-icon>
                  </template>
                </v-text-field>
              </template>
              <v-date-picker
                v-model="effectiveDate"
                :min="separationMinDate"
                @input="effectiveDateMenu = false"
              />
            </v-menu>
          </v-col>
         
          <v-col
            cols="12"
            class="pb-0"
          >
            <v-autocomplete
              v-model="separationReason"
              v-validate="'required'"
              :items="filteredSeparationReasons"
              label="Separation Reason"
              :error-messages="errors.collect('separationReason')"
              data-vv-name="separationReason" 
              data-vv-as="separation reason" 
              outlined
              item-text="reason"
              item-value="id"
            />
          </v-col>   

          <v-col
            cols="12"
            class="pb-0"
          >
            <helper-file-input
              v-model="file" 
              :max-size-in-mb="10"
              :max-number="5"
              :multiple="true"
              data-vv-name="files"
              field-name="files"
              accept-type=".pdf"     
              extension="ext:pdf"        
            >
              <div 
                slot="activator"
                class="mb-2"
              >   
                <v-btn                    
                  :disabled="progressing"
                  class="mx-2"    
                >
                  Add file
                </v-btn>
                <span> 
                  {{ file ? '' : 'No file chosen (.pdf only)' }}
                </span>
                <div
                  v-if="file"
                  class="mt-4"
                >
                  <p
                    v-for="selectedFile in file.selectedFiles"
                    :key="selectedFile.name"
                    class="mb-0"
                  >
                    <span class="font-weight-bold">{{ selectedFile.name }}</span>
                  </p>
                </div>
              </div>
            </helper-file-input>
            <span class="red--text text--lighten-1">{{ errors.first('files') }}</span>
          </v-col>
        </v-row>
      </v-container>

      <v-card-actions v-if="dialog && !loading">
        <v-spacer />
        <v-btn 
          text
          class="px-10"  
          @click="dialog = false"
        >
          close
        </v-btn>
        <v-btn 
          :loading="progressing"
          color="hrissecondary" 
          class="white--text px-10"  
          @click="submitForm"
        >
          Resign
        </v-btn>
      </v-card-actions>
    </material-card>
  </v-dialog>
</template>

<script>
  export default {
    components: {
      HelperFileInput: () => import('@/components/hris/helper/FileInput'),
    },
    $_veeValidate: {
      validator: 'new', 
    },
    props: {
      employeeUid: {
        type: String,
        default: '',
      },
      separationMinDate: {
        type: String, 
        required: true,
      },
    },
    data () {
      return {
        dialog: false,
        progressing: false,
        loading: false,
        separationType: null,
        separationTypes: [],
        effectiveDate: null,
        lastWorkingDate: null,
        separationReason: null,
        file: null,
        separationReasons: [],
        lastWorkingDateMenu: false,
        effectiveDateMenu: false,
      }
    },
    computed: {
      filteredSeparationReasons () {
        if (!this.separationType) return []

        return this.separationReasons.filter(separationReason => {
          return separationReason.separation_type_id === this.separationType
        })
      },
      lastWorkingDateDisplay () {
        return this.lastWorkingDate ? this.$moment(this.lastWorkingDate, 'YYYY-MM-DD').format('DD MMM YYYY') : ''
      },
      effectiveDateDisplay () {
        return this.effectiveDate ? this.$moment(this.effectiveDate, 'YYYY-MM-DD').format('DD MMM YYYY') : ''
      },
    },
    watch: {
      dialog: function (val) {
        if (!val) return this.$resetData()

        this.loading = true

        Promise.all([
          this.$api.hrisSeparationTypes().list(),
          this.$api.separationReasons().list(),
        ]).then(([separationTypes, separationReasons]) => {
          this.separationTypes = separationTypes.data
          this.separationReasons = separationReasons.data

          this.loading = false
        })
      },
    },
    methods: {
      submitForm () {
        this.$validator.validateAll().then(result => {
          if (!result) return
          
          this.progressing = true

          this.$api.hrisEmployeeSeparations().create(this.employeeUid, this.prepareFormData())
            .then(res => {
              if (res.status >= 200 && res.status < 300) {
                this.$emit('created')
                this.$snack({ message: 'Employee resigned' })

                this.progressing = false
                this.dialog = false
              }
            })
            .catch(error => {
              if (error.response.status === 405) {
                this.$dialog({
                  title: 'Unable to resign',
                  message: error.response.data.status,
                  color: 'error',
                })
              }
            })
            .then(() => {
              this.progressing = false
            })
        })
      },

      prepareFormData () {
        if (this.file) {
          this.file.formData.set('separation_type_id', this.separationType)
          this.file.formData.set('separation_reason_id', this.separationReason)
          this.file.formData.set('effective_date', this.effectiveDate)
          this.file.formData.set('last_working_date', this.lastWorkingDate)

          return this.file.formData
        }

        let formData = new FormData()

        formData.append('separation_type_id', this.separationType)
        formData.append('separation_reason_id', this.separationReason)
        formData.append('effective_date', this.effectiveDate)
        formData.append('last_working_date', this.lastWorkingDate)

        return formData
      },
    },
  }
</script>

<style scoped>
.label-position >>> .v-label.v-label--active{
  margin-left:-28px
}
</style>
